export interface DashboardEnv {
  apiBaseUrl: string;
  keycloakIssuerUrl: string;
  keycloakClientId: string;
  keycloakRealm: string;
  appEnv: string;
  sentryDsn: string | null;
  sentryRelease: string | null;
  sentryTracesSampleRate: number;
  sentryReplaysSessionSampleRate: number;
  sentryReplaysErrorSampleRate: number;
}

const API_VERSION_PREFIX = '/v1';
type DashboardIdentityConfig = Pick<
  DashboardEnv,
  'keycloakIssuerUrl' | 'keycloakClientId' | 'keycloakRealm'
>;

function parsePublicIdentityConfig(
  input: unknown,
  legacy = false,
): DashboardIdentityConfig {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    throw new Error('Invalid public identity configuration.');
  }
  const value = input as Record<string, unknown>;
  const clientId = requireEnvValue(
    typeof value.clientId === 'string' ? value.clientId : undefined,
    'Identity client ID',
  );
  const realm = requireEnvValue(
    typeof value.realm === 'string' ? value.realm : undefined,
    'Identity realm',
  );
  const issuer = new URL(
    requireEnvValue(
      typeof value.issuerUrl === 'string' ? value.issuerUrl : undefined,
      'Identity issuer',
    ),
  );
  const loopback = ['localhost', '127.0.0.1', '[::1]'].includes(
    issuer.hostname,
  );
  if (
    !/^zektec-space-(dev|stage|prod|local)$/u.test(realm) ||
    (!legacy && realm === 'zektec-space-local' && !loopback) ||
    issuer.pathname.replace(/\/$/u, '') !== `/auth/realms/${realm}` ||
    issuer.username ||
    issuer.password ||
    issuer.search ||
    issuer.hash ||
    (issuer.protocol !== 'https:' && !(issuer.protocol === 'http:' && loopback))
  ) {
    throw new Error('Invalid public identity realm or issuer.');
  }
  return {
    keycloakIssuerUrl: issuer.toString().replace(/\/$/u, ''),
    keycloakClientId: clientId,
    keycloakRealm: realm,
  };
}

function normalizeAbsoluteUrl(
  value: string | undefined,
  fieldName: string,
  fallback?: string,
) {
  const candidate = value?.trim() || fallback;
  if (!candidate) {
    throw new Error(`${fieldName} is required.`);
  }

  try {
    return new URL(candidate).toString().replace(/\/$/u, '');
  } catch {
    throw new Error(`${fieldName} must be a valid absolute URL.`);
  }
}

function requireEnvValue(value: string | undefined, fieldName: string) {
  const candidate = value?.trim();
  if (!candidate) {
    throw new Error(`${fieldName} is required.`);
  }

  return candidate;
}

function normalizeOptionalSampleRate(
  value: string | undefined,
  fieldName: string,
  fallback: number,
) {
  const candidate = value?.trim();
  if (!candidate) {
    return fallback;
  }

  const parsed = Number(candidate);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    throw new Error(`${fieldName} must be a number between 0 and 1.`);
  }

  return parsed;
}

export function parseDashboardEnv(
  source: Record<string, string | undefined>,
): DashboardEnv {
  const sentryDsn = source.VITE_SENTRY_DSN?.trim();
  const legacyValues = [
    source.VITE_KEYCLOAK_ISSUER_URL?.trim(),
    source.VITE_KEYCLOAK_CLIENT_ID?.trim(),
    source.VITE_KEYCLOAK_REALM?.trim(),
  ];
  if (legacyValues.some(Boolean) && !legacyValues.every(Boolean)) {
    throw new Error(
      'Legacy VITE_KEYCLOAK identity settings must be provided together.',
    );
  }
  const legacyIdentity = legacyValues.every(Boolean)
    ? parsePublicIdentityConfig(
        {
          issuerUrl: legacyValues[0],
          clientId: legacyValues[1],
          realm: legacyValues[2],
        },
        true,
      )
    : { keycloakIssuerUrl: '', keycloakClientId: '', keycloakRealm: '' };

  return {
    apiBaseUrl: normalizeAbsoluteUrl(
      source.VITE_API_BASE_URL,
      'VITE_API_BASE_URL',
      'http://127.0.0.1:3105',
    ),
    ...legacyIdentity,
    appEnv: source.VITE_APP_ENV?.trim() || 'development',
    sentryDsn: sentryDsn
      ? normalizeAbsoluteUrl(sentryDsn, 'VITE_SENTRY_DSN')
      : null,
    sentryRelease: source.VITE_SENTRY_RELEASE?.trim() || null,
    sentryTracesSampleRate: normalizeOptionalSampleRate(
      source.VITE_SENTRY_TRACES_SAMPLE_RATE,
      'VITE_SENTRY_TRACES_SAMPLE_RATE',
      0.1,
    ),
    sentryReplaysSessionSampleRate: normalizeOptionalSampleRate(
      source.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      'VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE',
      0,
    ),
    sentryReplaysErrorSampleRate: normalizeOptionalSampleRate(
      source.VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE,
      'VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE',
      1,
    ),
  };
}

let dashboardEnvCache: DashboardEnv | null = null;
let identityConfigPromise: Promise<DashboardEnv> | null = null;

export function getDashboardEnv() {
  if (dashboardEnvCache === null) {
    dashboardEnvCache = parseDashboardEnv(
      (import.meta.env ?? {}) as Record<string, string | undefined>,
    );
  }

  return dashboardEnvCache;
}

/** Load public metadata before sign-in; retain explicit legacy values only for an older API. */
export function hydrateDashboardIdentityConfig(
  fetchImpl: typeof fetch = fetch,
): Promise<DashboardEnv> {
  const env = getDashboardEnv();
  identityConfigPromise ??= (async () => {
    const response = await fetchImpl(buildApiUrl('/admin/auth/config'), {
      headers: { accept: 'application/json' },
      cache: 'no-store',
      redirect: 'error',
      signal: AbortSignal.timeout(10000),
    });
    if (
      response.status === 404 &&
      env.keycloakIssuerUrl &&
      env.keycloakClientId &&
      env.keycloakRealm
    ) {
      return env;
    }
    if (!response.ok)
      throw new Error('Public identity configuration is unavailable.');
    Object.assign(env, parsePublicIdentityConfig(await response.json()));
    return env;
  })().catch((error: unknown) => {
    identityConfigPromise = null;
    throw error;
  });
  return identityConfigPromise;
}

export function buildApiUrlFromBase(apiBaseUrl: string, path: string) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const basePathname = new URL(apiBaseUrl).pathname.replace(/\/$/u, '');
  const baseIncludesApiVersion =
    basePathname === API_VERSION_PREFIX ||
    basePathname.startsWith(`${API_VERSION_PREFIX}/`);
  const versionedPath =
    baseIncludesApiVersion ||
    normalizedPath === API_VERSION_PREFIX ||
    normalizedPath.startsWith(`${API_VERSION_PREFIX}/`)
      ? normalizedPath
      : `${API_VERSION_PREFIX}${normalizedPath}`;

  return `${apiBaseUrl}${versionedPath}`;
}

export function buildApiUrl(path: string) {
  return buildApiUrlFromBase(getDashboardEnv().apiBaseUrl, path);
}
